import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Bell, Plus, Calendar, AlertTriangle, CheckCircle2, Trash2, Clock, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";

const API = `${window.location.origin}/api`;

const PRIORITY_COLORS = {
  high: "bg-rose-500 text-white",
  medium: "bg-amber-500 text-white",
  low: "bg-slate-500 text-white",
};
const PRIORITY_LABELS = { high: "عالية", medium: "متوسطة", low: "منخفضة" };

export default function RemindersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | completed | overdue
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    due_date: "",
    priority: "medium",
    related_type: "general",
    related_name: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/reminders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.reminders || []);
    } catch (e) {
      toast.error("فشل جلب التذكيرات");
    } finally {
      setLoading(false);
    }
  };

  const createReminder = async () => {
    if (!form.title.trim() || !form.due_date) {
      toast.error("العنوان والتاريخ مطلوبان");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...form,
        due_date: new Date(form.due_date).toISOString(),
      };
      await axios.post(`${API}/reminders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تمت إضافة التذكير");
      setOpen(false);
      setForm({ title: "", description: "", due_date: "", priority: "medium", related_type: "general", related_name: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الإضافة");
    }
  };

  const toggleComplete = async (item) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API}/reminders/${item.id}`,
        { completed: !item.completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems((arr) =>
        arr.map((x) => (x.id === item.id ? { ...x, completed: !item.completed } : x))
      );
    } catch (e) {
      toast.error("فشل التحديث");
    }
  };

  const deleteReminder = async (id) => {
    if (!window.confirm("حذف التذكير؟")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/reminders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تم الحذف");
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("فشل الحذف");
    }
  };

  const now = new Date();
  const filtered = useMemo(() => {
    return items.filter((x) => {
      const due = new Date(x.due_date);
      if (filter === "pending") return !x.completed;
      if (filter === "completed") return x.completed;
      if (filter === "overdue") return !x.completed && due < now;
      return true;
    });
  }, [items, filter]);

  const stats = useMemo(() => {
    const pending = items.filter((x) => !x.completed).length;
    const overdue = items.filter((x) => !x.completed && new Date(x.due_date) < now).length;
    const completed = items.filter((x) => x.completed).length;
    return { pending, overdue, completed, total: items.length };
  }, [items]);

  return (
    <div className="space-y-6" data-testid="reminders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bell className="w-6 h-6 text-cyan-400" />
            التذكيرات
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            تذكيرات شخصية ومرتبطة بالعملاء أو الفواتير
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="border-slate-600 text-slate-200 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4 me-2" />
            تحديث
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-600 hover:bg-cyan-700" data-testid="add-reminder-btn">
                <Plus className="w-4 h-4 me-2" />
                تذكير جديد
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" dir="rtl">
              <DialogHeader>
                <DialogTitle>إضافة تذكير</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300">العنوان *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                    placeholder="مثال: متابعة دفعة العميل أحمد"
                    data-testid="reminder-title-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">التفاصيل (اختياري)</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300">التاريخ والوقت *</Label>
                    <Input
                      type="datetime-local"
                      value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                      data-testid="reminder-date-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">الأولوية</Label>
                    <select
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white"
                    >
                      <option value="low">منخفضة</option>
                      <option value="medium">متوسطة</option>
                      <option value="high">عالية</option>
                    </select>
                  </div>
                </div>
                <Button
                  onClick={createReminder}
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  data-testid="save-reminder-btn"
                >
                  حفظ
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-4 flex items-center gap-3">
            <Bell className="w-8 h-8 text-slate-400" />
            <div>
              <div className="text-xs text-slate-400">الكل</div>
              <div className="text-2xl font-bold text-white">{stats.total}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 text-amber-400" />
            <div>
              <div className="text-xs text-slate-400">قيد الانتظار</div>
              <div className="text-2xl font-bold text-white">{stats.pending}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-rose-400" />
            <div>
              <div className="text-xs text-slate-400">متأخرة</div>
              <div className="text-2xl font-bold text-rose-400">{stats.overdue}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            <div>
              <div className="text-xs text-slate-400">مكتملة</div>
              <div className="text-2xl font-bold text-emerald-400">{stats.completed}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: "الكل" },
          { key: "pending", label: "قيد الانتظار" },
          { key: "overdue", label: "متأخرة" },
          { key: "completed", label: "مكتملة" },
        ].map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={filter === f.key ? "default" : "outline"}
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? "bg-cyan-600 hover:bg-cyan-700" : "border-slate-600 text-slate-200"}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* List */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardContent className="p-4">
          {loading ? (
            <div className="text-center text-slate-400 py-8">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-400 py-12" data-testid="reminders-empty">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-40" />
              لا توجد تذكيرات في هذا القسم
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((r) => {
                const isOverdue = !r.completed && new Date(r.due_date) < now;
                return (
                  <div
                    key={r.id}
                    className={`border rounded-lg p-4 transition ${
                      r.completed ? "border-emerald-500/30 bg-emerald-500/5 opacity-70"
                      : isOverdue ? "border-rose-500/40 bg-rose-500/5"
                      : "border-slate-700 hover:bg-slate-700/30"
                    }`}
                    data-testid={`reminder-row-${r.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => toggleComplete(r)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                              r.completed ? "bg-emerald-500 border-emerald-500" : "border-slate-500 hover:border-emerald-400"
                            }`}
                            data-testid={`toggle-reminder-${r.id}`}
                          >
                            {r.completed && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </button>
                          <span className={`font-semibold ${r.completed ? "line-through text-slate-400" : "text-white"}`}>
                            {r.title}
                          </span>
                          <Badge className={PRIORITY_COLORS[r.priority]}>
                            {PRIORITY_LABELS[r.priority]}
                          </Badge>
                          {isOverdue && <Badge className="bg-rose-500 text-white">متأخر</Badge>}
                        </div>
                        {r.description && (
                          <p className="text-sm text-slate-400 mt-1 pr-7">{r.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 pr-7">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(r.due_date).toLocaleString("ar-IQ")}
                          </span>
                          {r.related_name && <span>📎 {r.related_name}</span>}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteReminder(r.id)}
                        className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                        data-testid={`delete-reminder-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
